import { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSyncStore } from '@/stores/sync.store';
import { useAuthStore } from '@/stores/auth.store';
import { useUIStore } from '@/stores/ui.store';
import { MapPin, Menu, LogOut, ShieldCheck, RefreshCw, WifiOff, CheckCircle2, Cloud, CloudOff, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { CustomSelect } from '../common/CustomSelect';
import { useBranches } from '../../db/hooks/useLookupData';
import { runManualSync } from '@/db/sync/replication';
import { useDatabase } from '@/db/DatabaseProvider';

/* ─── Route → Title Map ─── */
const ROUTE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/customers': 'Clientes',
  '/staff': 'Personal',
  '/tasas': 'Tasas de Cambio',
  '/settings': 'Configuración',
  '/usuarios': 'Usuarios',
};

function getPageTitle(pathname: string): string {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];
  const base = '/' + pathname.split('/').filter(Boolean)[0];
  return ROUTE_TITLES[base] ?? 'Depilaway';
}

/* ─── Header Component ─── */
export function Header() {
  const { pathname } = useLocation();
  const db = useDatabase();
  const toggleMobileDrawer = useUIStore((s) => s.toggleMobileDrawer);
  const { user, profile, isOwner, selectedBranchId, setSelectedBranchId, signOut } = useAuthStore();
  const { activeBranches } = useBranches();

  // Sync Store State
  const navigatorOnline = useSyncStore((s) => s.navigatorOnline);
  const isOnline = useSyncStore((s) => s.isOnline);
  const isSyncing = useSyncStore((s) => s.isSyncing);
  const autoSyncEnabled = useSyncStore((s) => s.autoSyncEnabled);
  const toggleAutoSync = useSyncStore((s) => s.toggleAutoSync);
  const pendingCount = useSyncStore((s) => s.pendingCount);
  const pendingItems = useSyncStore((s) => s.pendingItems);
  const lastSyncAt = useSyncStore((s) => s.lastSyncAt);
  const errors = useSyncStore((s) => s.errors);
  const clearErrors = useSyncStore((s) => s.clearErrors);

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showPendingDetails, setShowPendingDetails] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const title = getPageTitle(pathname);

  // Close user dropdown menu when clicking outside
  useEffect(() => {
    if (!isUserMenuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  // Filter active branches to only show what the user has access to
  const userBranches = useMemo(() => {
    return (activeBranches || []).filter((s) => {
      if (isOwner) return true;
      return profile?.assigned_branches?.includes(s.id);
    });
  }, [activeBranches, isOwner, profile?.assigned_branches]);

  const firstBranchId = userBranches[0]?.id;

  useEffect(() => {
    if (!selectedBranchId && firstBranchId) {
      setSelectedBranchId(firstBranchId);
    }
  }, [selectedBranchId, firstBranchId, setSelectedBranchId]);

  const userInitials = user?.email
    ? user.email
        .split('@')[0]
        .split(/[._-]/)
        .slice(0, 2)
        .map((s) => s.charAt(0).toUpperCase())
        .join('')
    : 'U';

  const userDisplayName = profile?.name || user?.email?.split('@')[0] || 'Usuario';
  const userRoleBadge = isOwner ? 'Propietaria' : profile?.job_title || 'Operaciones';

  /* ── Status Dot & Tooltip Config ── */
  let statusDotColor: string;
  let statusText: string;

  const hasErrors = errors.length > 0;

  if (!isOnline) {
    statusDotColor = 'bg-error shadow-glow';
    statusText = 'Sin conexión a internet (Guardado en este equipo)';
  } else if (hasErrors) {
    statusDotColor = 'bg-error shadow-glow animate-pulse';
    statusText = `${errors.length} error(es) de sincronización`;
  } else if (isSyncing) {
    statusDotColor = 'bg-brand-orange shadow-glow-orange';
    statusText = 'Guardando en la nube...';
  } else if (!autoSyncEnabled) {
    statusDotColor = 'bg-amber-500';
    statusText = 'Respaldo automático en pausa';
  } else if (pendingCount > 0) {
    statusDotColor = 'bg-brand-sky';
    statusText = `${pendingCount} cambio(s) en este equipo`;
  } else {
    statusDotColor = 'bg-emerald-500 shadow-sm';
    statusText = lastSyncAt
      ? `Guardado en la nube (${new Date(lastSyncAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`
      : 'Todo guardado en la nube';
  }

  return (
    <header className="glass-header sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between px-4 sm:px-6 backdrop-blur-xl bg-surface/85 border-b border-border/70 shadow-xs transition-all">
      {/* ── Left: Mobile menu + Title ── */}
      <div className="flex items-center gap-3">
        {/* Mobile hamburger button */}
        <button
          type="button"
          onClick={toggleMobileDrawer}
          className="btn-press flex h-9 w-9 items-center justify-center rounded-xl text-text-secondary transition-all hover:bg-surface-tertiary hover:text-text-primary md:hidden border border-border/50"
          aria-label="Abrir menú de navegación"
        >
          <Menu className="h-5 w-5 text-text-primary" />
        </button>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline-block h-2 w-2 rounded-full bg-brand-orange shadow-glow-orange" />
          <h1 className="text-base sm:text-lg font-bold tracking-tight text-text-primary">
            {title}
          </h1>
        </div>
      </div>

      {/* ── Right: Branch Switcher + Circular Avatar with Bottom Status Notification ── */}
      <div className="flex items-center gap-3">
        {/* Sede Switcher */}
        {userBranches.length > 1 ? (
          <CustomSelect
            value={selectedBranchId || ''}
            onChange={(val) => setSelectedBranchId(val || null)}
            options={userBranches.map((b) => ({ value: b.id, label: b.name }))}
            icon={<MapPin className="h-3.5 w-3.5 text-brand-orange" />}
            buttonClassName="!bg-surface-tertiary/70 !px-3 !py-1 !h-9 !border-border/80 hover:!bg-surface-tertiary font-bold text-xs"
            className="w-36 sm:w-44"
          />
        ) : userBranches.length === 1 ? (
          <div className="flex items-center gap-1.5 rounded-xl border border-border/80 bg-surface-tertiary/70 px-3 py-1.5 text-xs font-bold text-text-primary shadow-2xs">
            <MapPin className="h-3.5 w-3.5 text-brand-orange" />
            <span className="truncate max-w-[120px] sm:max-w-none">{userBranches[0].name}</span>
          </div>
        ) : null}

        {/* Circular Avatar with Bottom Status Notification Badge */}
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="btn-press relative flex items-center justify-center rounded-full p-0.5 transition-all cursor-pointer group focus:outline-hidden"
            aria-label="Menú de usuario y status"
            title={`Conectado como ${userDisplayName} — ${statusText}`}
          >
            {/* Avatar Circle */}
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-orange to-brand-deep text-xs font-black text-white shadow-sm ring-2 ring-brand-orange/40 group-hover:ring-brand-orange transition-all">
              {userInitials}
            </div>

            {/* Bottom-Right Corner Status Notification Dot */}
            <span className="absolute bottom-0 right-0 flex h-3 w-3 items-center justify-center rounded-full ring-2 ring-surface">
              {isSyncing && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-orange opacity-75" />
              )}
              <span className={`block h-2.5 w-2.5 rounded-full ${statusDotColor}`} />
            </span>
          </button>

          {/* User & Sync Status Dropdown Panel */}
          {isUserMenuOpen && (
            <div className="absolute right-0 mt-2 w-72 rounded-2xl border border-border bg-surface shadow-xl z-50 p-2.5 space-y-2.5 animate-scale-in">
              {/* Profile Info Header */}
              <div className="px-3 py-2 border-b border-border/60">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-extrabold text-text-primary truncate">{userDisplayName}</p>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-brand-orange/10 text-brand-orange border border-brand-orange/20 shrink-0">
                    <ShieldCheck className="h-3 w-3" />
                    <span>{userRoleBadge}</span>
                  </span>
                </div>
                <p className="text-[11px] text-text-tertiary truncate mt-0.5">{user?.email}</p>
              </div>

              {/* ── Auto-Sync Toggle Control ── */}
              <div className="px-3 py-2 rounded-xl bg-surface-tertiary/40 border border-border/50 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {autoSyncEnabled ? (
                    <Cloud className="h-4 w-4 text-brand-orange shrink-0" />
                  ) : (
                    <CloudOff className="h-4 w-4 text-text-tertiary shrink-0" />
                  )}
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-bold text-text-primary truncate">
                      Respaldo Automático
                    </span>
                    <span className="text-[10px] font-semibold text-text-tertiary truncate">
                      {autoSyncEnabled ? 'Guardado automático en la nube' : 'Pausado (Guardado en este equipo)'}
                    </span>
                  </div>
                </div>

                {/* Toggle Switch */}
                <button
                  type="button"
                  onClick={toggleAutoSync}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                    autoSyncEnabled ? 'bg-brand-orange' : 'bg-surface-tertiary'
                  }`}
                  role="switch"
                  aria-checked={autoSyncEnabled}
                  title={autoSyncEnabled ? 'Pausar respaldo automático' : 'Activar respaldo automático'}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out ${
                      autoSyncEnabled ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* ── Active Sync Errors Alert ── */}
              {hasErrors && (
                <div className="px-3 py-2 rounded-xl bg-error/10 border border-error/30 space-y-2">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <AlertCircle className="h-3.5 w-3.5 text-error shrink-0" />
                      <span className="font-bold text-error text-[11px] truncate">
                        {errors.length} error(es) de sincronización
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowErrorDetails(!showErrorDetails)}
                      className="text-[11px] font-bold text-error hover:underline flex items-center gap-0.5 cursor-pointer shrink-0"
                    >
                      <span>{showErrorDetails ? 'Ocultar' : 'Ver'}</span>
                      {showErrorDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                  </div>

                  {showErrorDetails && (
                    <div className="mt-1 max-h-36 overflow-y-auto space-y-1.5 pr-1 border-t border-error/20 pt-2 custom-scrollbar">
                      {errors.map((err, idx) => (
                        <div
                          key={`${err.timestamp}-${idx}`}
                          className="text-[11px] px-2 py-1.5 rounded-lg bg-surface/90 border border-error/20 space-y-0.5"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[9px] font-extrabold uppercase px-1.5 py-0.2 rounded bg-error/15 text-error">
                              {err.collection}
                            </span>
                            <span className="text-[9px] text-text-tertiary">
                              {new Date(err.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                          </div>
                          <p className="font-medium text-text-primary text-[10px] break-words line-clamp-2" title={err.message}>
                            {err.message}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-0.5">
                    <button
                      type="button"
                      onClick={() => {
                        clearErrors();
                        void runManualSync(db);
                      }}
                      disabled={isSyncing || !navigatorOnline}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded-lg bg-error text-white text-[10px] font-bold transition-all hover:bg-error/90 disabled:opacity-50 cursor-pointer"
                    >
                      <RefreshCw className={`h-3 w-3 ${isSyncing ? 'animate-spin' : ''}`} />
                      <span>Reintentar</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => clearErrors()}
                      className="px-2 py-1 rounded-lg border border-border bg-surface text-text-secondary text-[10px] font-bold transition-all hover:bg-surface-tertiary cursor-pointer"
                      title="Descartar errores"
                    >
                      Descartar
                    </button>
                  </div>
                </div>
              )}

              {/* ── Pending Changes Inspector ── */}
              <div className="px-3 py-2 rounded-xl bg-surface-tertiary/40 border border-border/50 space-y-2">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-1.5">
                    {!isOnline ? (
                      <WifiOff className="h-3.5 w-3.5 text-error shrink-0" />
                    ) : hasErrors ? (
                      <AlertCircle className="h-3.5 w-3.5 text-error shrink-0" />
                    ) : isSyncing ? (
                      <RefreshCw className="h-3.5 w-3.5 text-brand-orange animate-spin shrink-0" />
                    ) : (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    )}
                    <span className="font-semibold text-text-secondary text-[11px]">
                      {hasErrors
                        ? `${errors.length} error(es) pendientes`
                        : pendingCount > 0
                        ? `${pendingCount} cambio(s) por subir`
                        : 'Todo guardado en la nube'}
                    </span>
                  </div>

                  {pendingCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowPendingDetails(!showPendingDetails)}
                      className="text-[11px] font-bold text-brand-orange hover:underline flex items-center gap-0.5 cursor-pointer"
                    >
                      <span>{showPendingDetails ? 'Ocultar' : 'Ver'}</span>
                      {showPendingDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                  )}
                </div>

                {/* Expanded Pending Items List */}
                {pendingCount > 0 && showPendingDetails && (
                  <div className="mt-2 max-h-36 overflow-y-auto space-y-1 pr-1 border-t border-border/40 pt-2 custom-scrollbar">
                    {pendingItems.map((item, idx) => (
                      <div
                        key={`${item.id}-${idx}`}
                        className="flex items-center justify-between text-[11px] px-2 py-1 rounded-lg bg-surface/70 border border-border/30"
                      >
                        <span className="font-semibold text-text-primary truncate max-w-[170px]" title={item.label}>
                          {item.label}
                        </span>
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-brand-orange/10 text-brand-orange">
                          Guardado local
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Sync Now Action Button */}
                <button
                  type="button"
                  onClick={() => void runManualSync(db)}
                  disabled={isSyncing || !navigatorOnline}
                  title={!navigatorOnline ? 'Sin conexión física a internet' : 'Guardar cambios locales en la nube'}
                  className="w-full mt-1.5 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-orange/15 hover:bg-brand-orange/25 text-brand-orange text-xs font-bold transition-all disabled:opacity-50 cursor-pointer"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
                  <span>Guardar cambios en la nube ahora</span>
                </button>
              </div>

              {/* Sign Out Action */}
              <button
                type="button"
                onClick={() => {
                  setIsUserMenuOpen(false);
                  void signOut();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-xs font-semibold text-error hover:bg-error/10 transition-colors cursor-pointer"
              >
                <LogOut className="h-4 w-4" />
                <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
